import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { LuMenu, LuX, LuChevronDown } from 'react-icons/lu';
import logo from '../../../assets/images/logo.svg';
import courierImg from '../../../assets/images/service-courier.webp';
import airportImg from '../../../assets/images/service-airport.webp';
import doorToDoorImg from '../../../assets/images/testimonials-bg.webp';
import packingImg from '../../../assets/images/courier-boxes.webp';
import { NAV_ITEMS } from '../../../constants/navigation';
import { EASE_OUT } from '../../motion';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import styles from './Header.module.css';

const MotionContainer = motion.create(Container);

/** Photo for each tile in the desktop services menu — same as the Home cards. */
const MENU_IMAGES: Record<string, string> = {
  '/services/courier': courierImg,
  '/services/airport': airportImg,
  '/services/door-to-door': doorToDoorImg,
  '/services/packing-and-shipping': packingImg,
};

const Header: React.FC = () => {
  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;
  const prefersReducedMotion = useReducedMotion();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Desktop services menu opens on hover (click and keyboard still work).
  // A short close delay lets the pointer cross the gap into the panel.
  const [servicesOpen, setServicesOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);
  const canHover = () => window.matchMedia('(hover: hover)').matches;
  const openServices = () => {
    if (!canHover()) return;
    window.clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const closeServices = () => {
    if (!canHover()) return;
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setServicesOpen(false), 150);
  };
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  // Close it after navigating to a service.
  useEffect(() => setServicesOpen(false), [location.pathname]);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Publish the real header height as a CSS variable so full-viewport sections
  // can size themselves to `100svh - var(--header-h)` and fit exactly beneath
  // the sticky header at every breakpoint (no hardcoded, drift-prone value).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    let raf = 0;
    // Defer the write to the next frame instead of mutating --header-h inside
    // the ResizeObserver callback. Writing synchronously there changes layout
    // (the hero is sized off --header-h), which re-enters the observer and
    // emits the benign "ResizeObserver loop completed with undelivered
    // notifications" warning — harmless, but it pops the CRA dev error overlay.
    // rAF also coalesces the observer + window-resize firing into one write.
    const setVar = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        document.documentElement.style.setProperty(
          '--header-h',
          `${el.offsetHeight}px`,
        );
      });
    };
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener('resize', setVar);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', setVar);
    };
  }, []);

  // Close the mobile menu whenever the route changes (e.g. a link inside it is
  // followed) so it never lingers over the new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  // While the menu is open, lock body scroll and let Escape dismiss it.
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <Navbar
      ref={headerRef}
      expand="lg"
      bg="white"
      sticky="top"
      className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}
      collapseOnSelect
    >
      <MotionContainer
        fluid="xl"
        className={styles.container}
        initial={prefersReducedMotion ? false : { opacity: 0, y: -16 }}
        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
      >
        <Navbar.Brand as={Link} to="/" className={styles.brand}>
          <motion.img
            src={logo}
            alt="Sahari Cargo"
            className={styles.logo}
            whileHover={prefersReducedMotion ? undefined : { scale: 1.06 }}
            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
          />
        </Navbar.Brand>

        <Button to="/contact" variant="primary" className={styles.cta}>
          Request-Quote
        </Button>

        {/* Mobile trigger — opens the full-screen menu modal (below lg only). */}
        <button
          type="button"
          className={styles.hamburger}
          aria-label="Open menu"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <Icon icon={LuMenu} className={styles.hamburgerIcon} />
        </button>

        {/* Desktop nav — Bootstrap keeps this inline at lg+ and hidden below lg
            (it is never expanded now that the toggle is gone; the modal is the
            mobile path instead). */}
        <Navbar.Collapse id="main-nav" className={styles.collapse}>
          <Nav className={`mx-auto ${styles.nav}`}>
            {NAV_ITEMS.map((item) =>
              item.children ? (
                <NavDropdown
                  key={item.label}
                  title={item.label}
                  id={`nav-${item.label}`}
                  className={`${styles.dropdown} ${styles.mega}`}
                  show={servicesOpen}
                  onToggle={(next: boolean) => setServicesOpen(next)}
                  onMouseEnter={openServices}
                  onMouseLeave={closeServices}
                >
                  {/* Mega menu: 2×2 photo tiles + a red "not sure?" card. */}
                  <div className={styles.megaGrid}>
                    <div className={styles.megaTiles}>
                      {item.children.map((child) => (
                        <NavDropdown.Item
                          key={child.to}
                          as={Link}
                          to={child.to}
                          active={location.pathname === child.to}
                          className={styles.megaTile}
                        >
                          {MENU_IMAGES[child.to] && (
                            <img src={MENU_IMAGES[child.to]} alt="" className={styles.megaImg} />
                          )}
                          <span className={styles.megaTitle}>{child.label}</span>
                          {child.subtitle && <span className={styles.megaSub}>{child.subtitle}</span>}
                        </NavDropdown.Item>
                      ))}
                    </div>
                    <div className={styles.megaSide}>
                      <span className={styles.megaLogo}>
                        <img src={logo} alt="Sahari Cargo" />
                      </span>
                      <p className={styles.megaSideTitle}>Not sure which service fits?</p>
                      <p className={styles.megaSideText}>
                        Tell us what you're sending and where — we'll suggest the right one and quote it.
                      </p>
                      <NavDropdown.Item as={Link} to="/book-shipment" className={styles.megaSideBtn}>
                        Get a free quote →
                      </NavDropdown.Item>
                    </div>
                  </div>
                </NavDropdown>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) => {
                    const hasQuery = item.to.includes('?');
                    const active = hasQuery
                      ? currentUrl === item.to
                      : isActive;
                    return [
                      'nav-link',
                      styles.navLink,
                      active ? styles.active : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                  }}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </Nav>
        </Navbar.Collapse>
      </MotionContainer>

      {createPortal(
        <AnimatePresence>
          {menuOpen && (
            <MobileMenu
              currentUrl={currentUrl}
              reduce={!!prefersReducedMotion}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </AnimatePresence>,
        document.body,
      )}
    </Navbar>
  );
};

/* ── Mobile menu drawer ───────────────────────────────────────────────
   A brand-red panel that slides in from the right over a dimmed page.
   Clicking the dim area (or the X, or Escape) closes it. Rendered through a
   portal on <body> so it escapes the sticky header's stacking context.

   Parent items with children (OUR SERVICES) DON'T navigate — they toggle an
   accordion of their sub-links; only the sub-links navigate. */
const MobileMenu: React.FC<{
  currentUrl: string;
  reduce: boolean;
  onClose: () => void;
}> = ({ currentUrl, reduce, onClose }) => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  return (
    <motion.div
      className={styles.menuOverlay}
      onClick={onClose}
      initial={reduce ? undefined : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={reduce ? undefined : { opacity: 0 }}
      transition={{ duration: 0.2, ease: EASE_OUT }}
    >
      <motion.aside
        className={styles.menuDrawer}
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        onClick={(e) => e.stopPropagation()}
        initial={reduce ? undefined : { x: '100%' }}
        animate={{ x: 0 }}
        exit={reduce ? undefined : { x: '100%' }}
        transition={{ duration: 0.3, ease: EASE_OUT }}
      >
        <div className={styles.menuHeader}>
          <button
            type="button"
            className={styles.menuClose}
            aria-label="Close menu"
            onClick={onClose}
          >
            <Icon icon={LuX} className={styles.menuCloseIcon} />
          </button>
        </div>

        <nav className={styles.menuNav}>
          <ul className={styles.menuList}>
            {NAV_ITEMS.map((item) =>
              item.children ? (
                <li key={item.label} className={styles.menuItem}>
                  <button
                    type="button"
                    className={styles.menuGroupToggle}
                    aria-expanded={openGroup === item.label}
                    onClick={() =>
                      setOpenGroup((g) => (g === item.label ? null : item.label))
                    }
                  >
                    <span>{item.label}</span>
                    <Icon
                      icon={LuChevronDown}
                      className={`${styles.menuChevron} ${
                        openGroup === item.label ? styles.menuChevronOpen : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {openGroup === item.label && (
                      <motion.ul
                        className={styles.menuSubList}
                        initial={reduce ? undefined : { height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={reduce ? undefined : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: EASE_OUT }}
                      >
                        {item.children.map((child) => (
                          <li key={child.to}>
                            <NavLink
                              to={child.to}
                              onClick={onClose}
                              className={styles.menuSubLink}
                            >
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              ) : (
                <li key={item.label} className={styles.menuItem}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onClose}
                    className={({ isActive }) => {
                      const active = item.to.includes('?')
                        ? currentUrl === item.to
                        : isActive;
                      return [
                        styles.menuLink,
                        active ? styles.menuLinkActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ');
                    }}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ),
            )}
          </ul>

          <Link to="/contact" className={styles.menuQuote} onClick={onClose}>
            Request-Quote
          </Link>
        </nav>
      </motion.aside>
    </motion.div>
  );
};

export default Header;
